<template>
  <div class="reference-number-edit">
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      :input-id="`${uid}-type`"
      :label="
        t('common.paramTypes', { param: t('referenceData.referenceNumbers') })
      "
      :errors="v$.typeId.$errors"
    >
      <RygenDropdown
        :id="inputId"
        v-model="internalValue.type"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :disabled="disabled"
        :options="types"
        append-to="body"
        data-key="id"
        option-label="name"
        filter
        :placeholder="
          t('common.paramTypes', { param: t('referenceData.referenceNumbers') })
        "
      />
    </InputWrapper>

    <InputWrapper
      :input-id="`${uid}-value`"
      :label="refNumLabel"
      :errors="v$.value.$errors"
    >
      <template #default="{ ariaProps, inputClasses, inputId, invalid }">
        <div class="reference-number-edit-value-container">
          <RygenDropdown
            v-if="
              clientReferenceNumberType?.referenceNumberType?.valueType ===
              ReferenceNumberValueType.DROPDOWN
            "
            :id="inputId"
            v-model="internalValue.value"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :disabled="disabled"
            :editable="
              clientReferenceNumberType.referenceNumberType.allowFreeformValue
            "
            :options="
              clientReferenceNumberType.referenceNumberType.valueOptions ?? []
            "
            append-to="body"
            option-label="label"
            option-value="value"
            filter
            :placeholder="refNumLabel"
          />
          <RygenCalendar
            v-else-if="isDateOrTimeType"
            :id="inputId"
            v-model="internalValue.valueAsDateTime"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :disabled="disabled"
            :show-time="isTimeType"
            :time-only="
              clientReferenceNumberType?.referenceNumberType?.valueType ===
              ReferenceNumberValueType.TIME
            "
            hour-format="12"
            :placeholder="refNumLabel"
          />
          <InputNumber
            v-else-if="
              clientReferenceNumberType?.referenceNumberType?.valueType ===
              ReferenceNumberValueType.NUMERIC
            "
            v-model.trim="internalValue.valueAsNumber"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :input-id="inputId"
            :disabled="disabled"
            :placeholder="refNumLabel"
          />
          <span
            v-else-if="isFreeformMultiChoice"
            class="flex flex-gap-1 flex-grow-1"
          >
            <RygenMultiSelect
              v-bind="ariaProps"
              :id="inputId"
              v-model="internalValue.valueAsArray"
              :class="inputClasses"
              :invalid="invalid"
              :disabled="disabled"
              :options="
                clientReferenceNumberType?.referenceNumberType?.valueOptions ??
                []
              "
              option-label="label"
              option-value="value"
              filter
              :placeholder="refNumLabel"
            />
            <InputText
              :id="inputId"
              v-model.trim="internalValue.value"
              v-bind="ariaProps"
              :class="inputClasses"
              :invalid="invalid"
              :disabled="disabled"
              :placeholder="refNumLabel"
            />
          </span>
          <RygenMultiSelect
            v-else-if="isMultiChoice"
            v-bind="ariaProps"
            :id="inputId"
            v-model="internalValue.valueAsArray"
            :class="inputClasses"
            :invalid="invalid"
            :disabled="disabled"
            :options="
              clientReferenceNumberType?.referenceNumberType?.valueOptions ?? []
            "
            option-label="label"
            option-value="value"
            filter
            :placeholder="refNumLabel"
          />
          <InputText
            v-else
            :id="inputId"
            v-model.trim="internalValue.value"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :disabled="disabled"
            :placeholder="refNumLabel"
          />
          <InlineRemoveButton
            :disabled="disabled"
            class="w-auto"
            @click="emit('remove', internalValue)"
          />
        </div>
      </template>
      <template #helpText="{ helpTextId }">
        <small
          v-if="realReferenceNumberType?.description || isFreeformMultiChoice"
          :id="helpTextId"
          class="input-wrapper-help font-light"
        >
          <template v-if="realReferenceNumberType?.description">
            {{ realReferenceNumberType.description }}
          </template>
          <br
            v-if="realReferenceNumberType?.description && isFreeformMultiChoice"
          />
          <template v-if="isFreeformMultiChoice">
            {{ t('referenceData.freeformMultichoiceHelp') }}
          </template>
        </small>
      </template>
    </InputWrapper>
  </div>
</template>

<script lang="ts" setup>
import { ValidationRule } from '@vuelidate/core';
import { computed, PropType, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  required,
  requiredIf,
  useModelValue,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  ClientReferenceNumberType,
  ReferenceNumberType,
  ReferenceNumberValueType,
} from '@rygen/rygen-ui/model';

import { useReferenceNumberValueTypes } from '@/composables';
import { ReferenceNumber } from '@/model';

import InlineRemoveButton from '@/components/ui/InlineRemoveButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenCalendar from '@rygen/rygen-ui/components/form/RygenCalendar.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import RygenMultiSelect from '@rygen/rygen-ui/components/form/RygenMultiSelect.vue';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';

const props = defineProps({
  modelValue: {
    type: ReferenceNumber,
    default: () => new ReferenceNumber(),
  },
  types: {
    type: Array as PropType<ReferenceNumberType[]>,
    default: () => [],
  },
  clientTypes: {
    type: Array as PropType<ClientReferenceNumberType[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { modelValue, types, clientTypes, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'remove', refNum: ReferenceNumber): void;
  (event: 'update:model-value', newVal: ReferenceNumber): void;
}>();

const { t } = useI18n();
const { uid } = useUid('reference-number');

const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<ReferenceNumber>) => new ReferenceNumber(opts),
});

const refNumLabel = computed(() => {
  return (
    internalValue.value?.typeName ??
    t('common.paramValues', { param: t('referenceData.referenceNumbers') })
  );
});

const clientReferenceNumberType = computed(() => {
  return clientTypes.value.find(
    (clientType) =>
      clientType.referenceNumberType?.naturalId &&
      clientType.referenceNumberType.naturalId ===
        internalValue.value.type?.naturalId,
  );
});

const realReferenceNumberType = computed(
  () => clientReferenceNumberType.value?.referenceNumberType,
);
const { isDateOrTimeType, isMultiChoice, isFreeformMultiChoice, isTimeType } =
  useReferenceNumberValueTypes(clientReferenceNumberType);

const rules = computed(() => {
  const r: Record<string, Record<string, ValidationRule>> = {
    typeId: {},
    value: {
      requiredIf: requiredIf(
        () => internalValue.value.type?.naturalId,
        t('common.paramTypes', { param: t('referenceData.referenceNumbers') }),
      ),
    } as Record<string, ValidationRule<any>>,
  };

  r.typeId.required = required(
    t('common.paramTypes', { param: t('referenceData.referenceNumbers') }),
  );

  return r;
});
const { v$ } = useValidations(internalValue, rules);

watch(
  realReferenceNumberType,
  (
    newVal: ReferenceNumberType | undefined,
    oldVal: ReferenceNumberType | undefined,
  ) => {
    const oldValIsDropdown =
      oldVal?.valueType === ReferenceNumberValueType.DROPDOWN ||
      oldVal?.valueType === ReferenceNumberValueType.MULTI_CHOICE;
    if (
      newVal?.valueType &&
      (newVal.valueType !== oldVal?.valueType || oldValIsDropdown)
    ) {
      internalValue.value.value = undefined;
      v$.value.$reset();
    }
  },
);
watch(
  () => internalValue.value.value,
  () => {
    internalValue.value.value = internalValue.value.value?.replace(
      /[^\0-\x7F]/g,
      '',
    );
  },
);
</script>

<style>
.reference-number-edit {
  display: flex;
  gap: var(--gap-1);

  .input-wrapper {
    flex: 1 0 auto;
    max-width: 50%;

    .reference-number-edit-value-container {
      display: flex;
      gap: var(--gap-1);
      align-items: center;

      & > :first-child {
        /* the input should grow to fill the container */
        flex: 1 0 auto;
      }
    }
  }
}
</style>
